import { FormValidator } from "@CubeComponents/Mui_Form/FormValidator";
import type { ValidationRule, ValidationRules } from "@CubeComponents/Mui_Form/FormValidator";
import { isTableSection, isWellFormedTableSection } from "@Helpers";
import { optionsViewType } from "@Features/portal-settings";
import { formStatuses } from "./constants";
import { FormControlLabel, RadioGroup } from "@mui/material";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import { getUserSelectionModeOptions } from "../FormFlow/formFlow.constants";
import type { ChangeEvent } from "react";
import type { QuestionField } from "@ServerTypes/forms/forms.schema";
import type { FormInstance, FormInstanceSection } from "../../formInstance.wire";

/** A file/folder row the caller wants a per-row copy of every rule for. */
interface ValidationTargetRow {
  id: string | number;
  isFolder?: boolean;
}

/** One row of a table section's `tableDataSource`; rules are keyed by `rowId` + question id. */
interface TableSectionRow {
  rowId?: string;
  [key: string]: unknown;
}

/**
 * The captured template as this helper can actually be handed it.
 *
 * `FormInstance` declares `selectedForm` and `selectedForm.template` REQUIRED (server
 * `forms/inspectionAndProgress.collection.ts`, `selectedFormSnapshot`), but nothing parses the
 * instance on the way in — it is whatever the caller passes — so the two reads below go through
 * this widened view. That keeps the guards this helper has always carried honest instead of
 * dropping them because a wire type promises the field is there.
 */
type CapturedTemplateSource = { selectedForm?: { template?: FormInstanceSection[] } };

interface AddValidationToInputFieldsParams {
  /** The live form instance. When it has an `_id`, its current level decides which sections validate. */
  formData?: FormInstance | null;
  template?: FormInstanceSection[];
  /** Ref the constructed validator is stored on. */
  formObject: { current: FormValidator | null };
  rows?: ValidationTargetRow[];
  modifySection?: Array<string | undefined>;
}

export const statusTemplate = (stage?: string) => {
  let status = "";
  let color;
  if (stage == formStatuses.inProgress) {
    status = "In progress";
    color = "bg-system_colour_12";
  }
  if (stage == formStatuses.submitted) {
    status = "Submitted";
    color = "bg-system_colour_45";
  }
  if (stage == formStatuses.approved) {
    status = "Approved";
    color = "bg-positive_communication";
  }
  if (stage == formStatuses.rejected) {
    status = "Rejected";
    color = "bg-system_colour_8";
  }
  return (
    <div className={`h-8 ${color} w-fit ps-3 gap-2 bg-opacity-30 rounded-2xl pe-3 flex items-center`}>
      <span className={`${color} rounded-full h-3 w-3`} />
      <span className="font-semibold">{status}</span>
    </div>
  );
};
export const addValidationToInputFields = ({
  formData,
  template = [],
  formObject,
  rows = [],
  modifySection = [],
}: AddValidationToInputFieldsParams) => {
  const allSectionsInCurrentLevel: Array<string | undefined> = [];
  const excludedTypes = ["users", "fileUpload", "image", "url"];
  if (formData && formData._id) {
    // Non-null throughout this branch: the level at `currentLevelOfForm`, its `dataSource` and each
    // row's `sections` are all read without a guard. The first is `noUncheckedIndexedAccess`
    // bookkeeping; the other two are `.optional()` on the wire and dereferenced anyway, so an
    // absent value throws TypeError. All three assertions are erased at emit, keeping the existing
    // runtime; guarding them would mean inventing an empty-rule path — a behaviour change.
    formData.formFlow[formData.currentLevelOfForm]!.dataSource!.map((data) => {
      if (data.type === "Fill") {
        data.sections!.map((section) => allSectionsInCurrentLevel.push(section._id));
      } else if (data.type === "Modify") {
        data.sections!.map((section) => {
          if (modifySection.includes(section._id)) {
            allSectionsInCurrentLevel.push(section._id);
          }
        });
      }
    });
    const instance: CapturedTemplateSource = formData;
    template = instance.selectedForm?.template ?? [];
  } else if (template.length) {
    template.forEach((section) => allSectionsInCurrentLevel.push(section._id));
  } else if (formData) {
    const instance: CapturedTemplateSource = formData;
    template = instance.selectedForm?.template ?? [];
    template.forEach((section) => allSectionsInCurrentLevel.push(section._id));
  }

  const options: { rules: ValidationRules } = { rules: {} };
  // `.forEach`, not `.some`: none of the four callbacks in this helper ever returns a value, so
  // none of them could ever short-circuit its iteration.
  template.forEach((section) => {
    if (isTableSection(section) && allSectionsInCurrentLevel.includes(section._id)) {
      // DEAD CODE — `addValidationToInputFields` has no caller anywhere in the repo (the only
      // reference is a commented-out line in useForms.tsx's return object). It is kept in step with
      // the two live validators rather than left to drift: the `?.` pair it used to carry is exactly
      // what the review flagged, because a missing array made the loop no-op and registered no rules
      // at all, so the section looked fully validated to the user. Delete this helper and the
      // divergence goes with it; until then it agrees with `checkMandatoryFields`.
      if (!isWellFormedTableSection(section)) return;
      section.questionFields.forEach((eachField: QuestionField) => {
        section.tableDataSource.forEach((row: TableSectionRow) => {
          const isSelectWithListView =
            (eachField.fieldType === "multi-select" || eachField.fieldType === "single-select") &&
            eachField.viewType === optionsViewType.LIST;
          if (!excludedTypes.includes(eachField.fieldType) && !isSelectWithListView) {
            const ruleObj: ValidationRule = {};
            const ruleName = `${row.rowId}${eachField._id}`;
            if (eachField.isMandatory === true) {
              ruleObj.required = [true, "* Required"];
            }
            if (["single-line", "multi-line"].includes(eachField.fieldType)) {
              // `[limit]`, not a bare number: `FormValidator.validateField` reads every rule as a
              // `[value, message]` tuple and takes `arr[0]`, so a bare number handed it `undefined`
              // and neither length check could ever fail.
              ruleObj.minLength = [eachField.minFieldLimit ?? 0];
              if (eachField.maxFieldLimit) {
                ruleObj.maxLength = [eachField.maxFieldLimit];
              }
            } else if (["date-time"].includes(eachField.fieldType)) {
              ruleObj.regex = [
                "^([0-2][0-9]|3[0-1])/((0[1-9])|(1[0-2]))/([1-9][0-9]{3}) ((0[1-9])|(1[0-2])):([0-5][0-9]):(AM|PM)$",
                "Please enter a valid date",
              ];
            }
            options.rules[ruleName] = {
              ...ruleObj,
            };
            if (Array.isArray(rows)) {
              rows.forEach((row) => {
                if (!row.isFolder) {
                  options.rules[`${row.id}_${ruleName}`] = {
                    ...ruleObj,
                  };
                }
              });
            } else {
              options.rules[ruleName] = {
                ...ruleObj,
              };
            }
          }
        });
      });
    } else if (section.questionFields) {
      if (allSectionsInCurrentLevel.includes(section._id)) {
        section.questionFields.forEach((eachField: QuestionField) => {
          const isSelectWithListView =
            (eachField.fieldType === "multi-select" || eachField.fieldType === "single-select") &&
            eachField.viewType === optionsViewType.LIST;
          if (!excludedTypes.includes(eachField.fieldType) && !isSelectWithListView) {
            const ruleName = `${eachField._id}`;
            const ruleObj: ValidationRule = {};
            if (eachField.isMandatory === true) {
              ruleObj.required = [true, "* Required"];
            }
            if (["single-line", "multi-line"].includes(eachField.fieldType)) {
              ruleObj.minLength = [eachField.minFieldLimit ?? 0];
              if (eachField.maxFieldLimit) {
                ruleObj.maxLength = [eachField.maxFieldLimit];
              }
            } else if (["date-time"].includes(eachField.fieldType)) {
              ruleObj.regex = [
                "^([0-2][0-9]|3[0-1])/((0[1-9])|(1[0-2]))/([1-9][0-9]{3}) ((0[1-9])|(1[0-2])):([0-5][0-9]):(AM|PM)$",
                "Please enter a valid date",
              ];
            }
            if (Array.isArray(rows)) {
              rows.forEach((row) => {
                if (!row.isFolder) {
                  options.rules[`${row.id}_${ruleName}`] = {
                    ...ruleObj,
                  };
                }
              });
            } else {
              options.rules[ruleName] = {
                ...ruleObj,
              };
            }
          }
        });
      }
    }
    formObject.current = new FormValidator("#formData", options);
  });
};
/**
 * "Who should be assigned?" radios. The options themselves live in
 * `FormFlow/formFlow.constants` so web and mobile offer exactly the same choices.
 */
export const getUserSelection = (
  heading: string,
  userSelectionMode: string,
  handleUserSelectionModeChange: (args: ChangeEvent<HTMLInputElement>) => void,
) => (
  <div className="flex flex-col gap-2">
    <span>{heading}</span>
    <span>
      <RadioGroup value={userSelectionMode} onChange={handleUserSelectionModeChange}>
        {getUserSelectionModeOptions().map((option) => (
          <FormControlLabel key={option.value} value={option.value} control={<RadioButton />} label={option.label} />
        ))}
      </RadioGroup>
    </span>
  </div>
);
